import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import toast from 'react-hot-toast';
import { FaMotorcycle, FaUsers, FaMapMarkerAlt, FaClock, FaStar, FaSearch, FaCalendarAlt, FaRoute } from 'react-icons/fa';
import { geocodeAddress, getCurrentLocation, reverseGeocode } from '../utils/maps';
import MapContainer from '../components/MapContainer';
import AutocompleteInput from '../components/AutocompleteInput';
import { FaCrosshairs } from 'react-icons/fa';
import { cleanAddress } from '../utils/addressUtils';

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [quickSearch, setQuickSearch] = useState({
    origin: '',
    destination: '',
    date: '',
    vehicleType: 'all'
  });
  const [originCoords, setOriginCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState({ origin: false, destination: false });

  const handleQuickSearch = async (e) => {
    e.preventDefault();
    
    // Trim whitespace and check if fields are actually filled
    const origin = quickSearch.origin?.trim();
    const destination = quickSearch.destination?.trim();
    
    if (!origin || origin.length === 0) {
      toast.error('Please enter an origin location');
      return;
    }
    
    if (!destination || destination.length === 0) {
      toast.error('Please enter a destination location');
      return;
    }

    setLoading(true);
    let hasErrors = false;
    
    try {
      // Use coordinates if available from autocomplete, otherwise geocode
      let originGeo, destGeo;
      
      // Handle origin
      if (originCoords) {
        originGeo = {
          lat: originCoords.lat,
          lng: originCoords.lng,
          formatted_address: origin
        };
      } else {
        try {
          originGeo = await geocodeAddress(origin);
        } catch (geoError) {
          hasErrors = true;
          toast.error(
            `Origin address "${origin}" not found.\n\n💡 Please select an address from the suggestions dropdown when typing.`,
            { duration: 6000 }
          );
          setLoading(false);
          return;
        }
      }

      // Handle destination
      if (destCoords) {
        destGeo = {
          lat: destCoords.lat,
          lng: destCoords.lng,
          formatted_address: destination
        };
      } else {
        try {
          destGeo = await geocodeAddress(destination);
        } catch (geoError) {
          hasErrors = true;
          toast.error(
            `Destination address "${destination}" not found.\n\n💡 Please select an address from the suggestions dropdown when typing.`,
            { duration: 6000 }
          );
          setLoading(false);
          return;
        }
      }

      // If we got here, both addresses were geocoded successfully
      const response = await api.get('/api/trips/search', {
        params: {
          origin: `${originGeo.lat},${originGeo.lng}`,
          destination: `${destGeo.lat},${destGeo.lng}`,
          ...(quickSearch.date && quickSearch.date.trim() !== '' && { date: quickSearch.date })
        }
      });
      
      // Filter by vehicle type on frontend
      let filteredTrips = response.data?.trips || response.data || [];
      if (!Array.isArray(filteredTrips)) filteredTrips = [];
      if (quickSearch.vehicleType !== 'all') {
        filteredTrips = filteredTrips.filter(trip => 
          trip.rider?.vehicleInfo?.type === quickSearch.vehicleType
        );
      }
      
      setTrips(Array.isArray(filteredTrips) ? filteredTrips : []);
      setShowResults(true);
      
      // Scroll to results
      setTimeout(() => {
        document.getElementById('search-results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
      if (filteredTrips.length > 0) {
        toast.success(`Found ${filteredTrips.length} trips`);
      } else {
        toast('No trips found for your search criteria', { icon: 'ℹ️' });
      }
    } catch (error) {
      console.error('Search error:', error);
      // Only show error if we haven't already shown a geocoding error
      if (!hasErrors) {
        const errorMsg = error.message || 'Failed to search trips. Please check your addresses.';
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOriginSelect = (place) => {
    setOriginCoords(place.coordinates);
  };

  const handleDestinationSelect = (place) => {
    setDestCoords(place.coordinates);
  };

  const handleBookTrip = (tripId) => {
    if (!isAuthenticated) {
      toast.error('Please login to book a trip');
      navigate('/login');
      return;
    }
    navigate(`/trip/${tripId}`);
  };

  const handleShowTripOnMap = (trip) => {
    setSelectedTrip(trip);
    setShowMap(true);
  };

  const handleUseCurrentLocation = async (field) => {
    setLoadingLocation(prev => ({ ...prev, [field]: true }));
    try {
      // Get current location
      const location = await getCurrentLocation();
      
      // Reverse geocode to get address
      const addressData = await reverseGeocode(location.lat, location.lng);
      
      // Update form data
      if (field === 'origin') {
        setQuickSearch(prev => ({ ...prev, origin: addressData.address }));
        setOriginCoords({ lat: addressData.lat, lng: addressData.lng });
        toast.success('Current location set as origin');
      } else {
        setQuickSearch(prev => ({ ...prev, destination: addressData.address }));
        setDestCoords({ lat: addressData.lat, lng: addressData.lng });
        toast.success('Current location set as destination');
      }
    } catch (error) {
      console.error('Error getting current location:', error);
      if (error.message.includes('denied')) {
        toast.error('Location access denied. Please enable location permissions in your browser settings.');
      } else if (error.message.includes('timeout')) {
        toast.error('Location request timed out. Please try again.');
      } else {
        toast.error('Failed to get current location. Please try again.');
      }
    } finally {
      setLoadingLocation(prev => ({ ...prev, [field]: false }));
    }
  };

  // Update document title and meta tags for SEO
  useEffect(() => {
    document.title = "SastaMastaRide - Affordable & Fun Ridesharing | Bike, Scooter & Car Sharing Platform";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'SastaMastaRide is India\'s leading affordable two-wheeler ridesharing platform. Connect with riders and passengers for shared rides on bikes, scooters, and cars. Find affordable rides, offer rides, and save money on your daily commute.');
    }
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-3 sm:px-4 text-center">
          {/* Login/Signup Buttons for Unauthenticated Users - Top Right */}
          {!isAuthenticated && (
            <div className="flex justify-end mb-4 sm:mb-6">
              <div className="flex gap-2 sm:gap-3">
                <Link 
                  to="/login" 
                  className="bg-white/10 hover:bg-white/20 text-white font-semibold border-2 border-white/50 px-4 sm:px-6 py-2 sm:py-2.5 rounded-md transition-colors text-sm sm:text-base min-h-[40px] flex items-center justify-center"
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="bg-white hover:bg-gray-100 text-blue-600 font-semibold px-4 sm:px-6 py-2 sm:py-2.5 rounded-md transition-colors text-sm sm:text-base min-h-[40px] flex items-center justify-center"
                >
                  Sign Up
                </Link>
              </div>
            </div>
          )}
          
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-4 sm:mb-6 px-2">
            Share Your Ride, Share Your Journey
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 text-blue-100 px-2">
            Connect with fellow riders and passengers for shared rides - bikes, scooters, and cars
          </p>
          
          {/* Quick Search Form */}
          <div className="max-w-4xl mx-auto mb-6 sm:mb-8 bg-white rounded-lg shadow-xl p-4 sm:p-6 relative z-0">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3 sm:mb-4">Find Your Perfect Ride</h2>
            <form onSubmit={handleQuickSearch} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 relative z-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaMapMarkerAlt className="inline mr-2 text-blue-600" />
                    From
                    {originCoords && <span className="ml-2 text-green-600">✓</span>}
                  </label>
                  <div className="relative">
                    <AutocompleteInput
                      name="origin"
                      value={quickSearch.origin}
                      onChange={(e) => {
                        setQuickSearch(prev => ({ ...prev, origin: e.target.value }));
                      }}
                      onPlaceSelect={(place) => {
                        setQuickSearch(prev => ({ ...prev, origin: place.address }));
                        setOriginCoords(place.coordinates);
                      }}
                      placeholder="Start typing to see suggestions..."
                      required
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                    />
                    <button
                      type="button"
                      onClick={() => handleUseCurrentLocation('origin')}
                      disabled={loadingLocation.origin}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Use Current Location"
                    >
                      {loadingLocation.origin ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      ) : (
                        <FaCrosshairs />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {originCoords ? '✓ Address selected' : '💡 Start typing for suggestions or use current location'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaMapMarkerAlt className="inline mr-2 text-red-600" />
                    To
                    {destCoords && <span className="ml-2 text-green-600">✓</span>}
                  </label>
                  <div className="relative">
                    <AutocompleteInput
                      name="destination"
                      value={quickSearch.destination}
                      onChange={(e) => {
                        setQuickSearch(prev => ({ ...prev, destination: e.target.value }));
                      }}
                      onPlaceSelect={(place) => {
                        setQuickSearch(prev => ({ ...prev, destination: place.address }));
                        setDestCoords(place.coordinates);
                      }}
                      placeholder="Start typing to see suggestions..."
                      required
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                    />
                    <button
                      type="button"
                      onClick={() => handleUseCurrentLocation('destination')}
                      disabled={loadingLocation.destination}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Use Current Location"
                    >
                      {loadingLocation.destination ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      ) : (
                        <FaCrosshairs />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {destCoords ? '✓ Address selected' : '💡 Start typing for suggestions or use current location'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaCalendarAlt className="inline mr-2 text-green-600" />
                    Date
                  </label>
                  <input
                    type="date"
                    value={quickSearch.date}
                    onChange={(e) => setQuickSearch(prev => ({ ...prev, date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaMotorcycle className="inline mr-2 text-purple-600" />
                    Vehicle Type
                  </label>
                  <select
                    value={quickSearch.vehicleType}
                    onChange={(e) => setQuickSearch(prev => ({ ...prev, vehicleType: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                  >
                    <option value="all">All Vehicles</option>
                    <optgroup label="Two Wheelers">
                      <option value="bike">Bike</option>
                      <option value="scooter">Scooter</option>
                    </optgroup>
                    <optgroup label="Four Wheelers">
                      <option value="car">Car</option>
                      <option value="sedan">Sedan</option>
                      <option value="hatchback">Hatchback</option>
                      <option value="suv">SUV</option>
                    </optgroup>
                  </select>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 sm:py-3 px-6 rounded-md transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base min-h-[44px]"
              >
                <FaSearch className="mr-2" />
                {loading ? 'Searching...' : 'Search Rides'}
              </button>
            </form>
          </div>

          {/* Offer a Ride Button for Authenticated Users */}
          {isAuthenticated && (
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2 mt-4">
              <Link to="/register" className="btn-secondary border-2 border-white text-white hover:bg-white hover:text-blue-600 text-base sm:text-lg px-6 sm:px-8 py-3 min-h-[44px] flex items-center justify-center">
                Offer a Ride
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Search Results Section */}
      {showResults && (
        <section id="search-results" className="py-8 sm:py-12 md:py-16 bg-gray-50">
          <div className="container mx-auto px-3 sm:px-4">
            <div className="max-w-6xl mx-auto">
              {trips.length > 0 ? (
                <>
                  <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
                      Available Rides ({trips.length})
                    </h2>
                    {selectedTrip && (
                      <button
                        onClick={() => setShowMap(!showMap)}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-4 py-2 rounded-md transition-colors flex items-center"
                      >
                        <FaRoute className="mr-2" />
                        {showMap ? 'Hide Map' : 'Show Map'}
                      </button>
                    )}
                  </div>

                  {/* Map View */}
                  {showMap && selectedTrip && (
                    <div className="mb-6 bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        Route: {cleanAddress(selectedTrip.origin.address)} → {cleanAddress(selectedTrip.destination.address)}
                      </h3>
                      <MapContainer
                        origin={selectedTrip.origin.coordinates}
                        destination={selectedTrip.destination.coordinates}
                        waypoints={selectedTrip.waypoints || []}
                        height="400px"
                        showRoute={true}
                      />
                      {selectedTrip.waypoints && selectedTrip.waypoints.length > 0 && (
                        <div className="mt-4 text-sm text-gray-600">
                          <p className="font-medium mb-2">Stops along the route:</p>
                          <ol className="list-decimal list-inside space-y-1">
                            {selectedTrip.waypoints
                              .sort((a, b) => (a.order || 0) - (b.order || 0))
                              .map((wp, idx) => (
                                <li key={idx}>{wp.address}</li>
                              ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Trips List */}
                  <div className="space-y-4 sm:space-y-6">
                    {trips.map((trip) => (
                      <div key={trip._id} className="bg-white rounded-lg shadow-md p-4 sm:p-6 hover:shadow-lg transition-shadow">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                          <div className="flex-1 w-full">
                            <div className="flex items-center mb-3">
                              <img
                                src={trip.rider?.photo || '/default-avatar.png'}
                                alt={trip.rider?.name || 'Rider'}
                                className="w-12 h-12 rounded-full mr-3 border-2 border-gray-200"
                              />
                              <div>
                                <h3 className="font-semibold text-gray-800 text-lg">
                                  {trip.rider?.name || 'Anonymous Rider'}
                                </h3>
                                <div className="flex items-center text-sm text-gray-600">
                                  <span className="mr-2">
                                    ⭐ {(trip.rider?.rating || 0).toFixed(1)}
                                  </span>
                                  <span className="mr-2">•</span>
                                  <span>{(trip.rider?.totalRides || 0)} rides</span>
                                  {trip.rider?.vehicleInfo?.type && (
                                    <>
                                      <span className="mr-2">•</span>
                                      <span className="capitalize">{trip.rider.vehicleInfo.type}</span>
                                      {trip.rider.vehicleInfo.seats && (
                                        <span className="ml-1">({trip.rider.vehicleInfo.seats} seats)</span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="mb-3">
                              <div className="flex items-center text-gray-700 mb-2">
                                <FaMapMarkerAlt className="text-green-500 mr-2" />
                                <span className="font-medium">{cleanAddress(trip.origin.address)}</span>
                              </div>
                              <div className="flex items-center text-gray-700">
                                <FaMapMarkerAlt className="text-red-500 mr-2" />
                                <span className="font-medium">{cleanAddress(trip.destination.address)}</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center text-sm text-gray-600 mb-2">
                              <FaClock className="mr-2" />
                              <span>{new Date(trip.departureTime).toLocaleString()}</span>
                              <span className="mx-2">•</span>
                              <span>{trip.availableSeats - trip.bookedSeats}/{trip.availableSeats} seats available</span>
                            </div>
                            
                            {trip.description && (
                              <p className="text-gray-600 text-sm mb-2">{trip.description}</p>
                            )}
                          </div>
                          
                          <div className="text-left sm:text-right w-full sm:w-auto sm:ml-6">
                            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-3">
                              ₹{user?.role === 'passenger' || !isAuthenticated ? 
                                (trip.price + Math.round(trip.price * 0.1 * 100) / 100) : 
                                trip.price}
                            </div>
                            <div className="space-y-2">
                              <button
                                onClick={() => handleBookTrip(trip._id)}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-md transition-colors w-full sm:w-auto min-h-[44px] text-sm sm:text-base"
                              >
                                Book Now
                              </button>
                              <button
                                onClick={() => handleShowTripOnMap(trip)}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-6 py-3 rounded-md transition-colors w-full sm:w-auto flex items-center justify-center min-h-[44px] text-sm sm:text-base"
                              >
                                <FaRoute className="mr-1" />
                                View Route
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg shadow-md">
                  <FaSearch className="mx-auto text-gray-400 text-6xl mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No rides found
                  </h3>
                  <p className="text-gray-500">
                    Try adjusting your search criteria or check back later for new rides.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="container mx-auto px-3 sm:px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12 text-gray-800">
            Why Choose SastaMastaRide?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaMotorcycle className="text-blue-600 text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Cost-Effective</h3>
              <p className="text-gray-600">
                Split the cost of fuel and reduce your daily commute expenses by sharing rides with others.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaUsers className="text-green-600 text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Meet New People</h3>
              <p className="text-gray-600">
                Connect with like-minded people in your area and build meaningful relationships.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaMapMarkerAlt className="text-purple-600 text-2xl" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Convenient Routes</h3>
              <p className="text-gray-600">
                Find rides that match your daily route and schedule with our smart matching system.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-3 sm:px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12 text-gray-800">
            How It Works
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="text-lg font-semibold mb-2">Sign Up</h3>
              <p className="text-gray-600">Create your account as a rider or passenger</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="text-lg font-semibold mb-2">Find or Offer</h3>
              <p className="text-gray-600">Search for rides or post your available trip</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="text-lg font-semibold mb-2">Book & Pay</h3>
              <p className="text-gray-600">Secure booking with instant payment processing</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                4
              </div>
              <h3 className="text-lg font-semibold mb-2">Ride Together</h3>
              <p className="text-gray-600">Enjoy your shared journey and rate your experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">1000+</div>
              <div className="text-blue-100">Active Users</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">5000+</div>
              <div className="text-blue-100">Successful Rides</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-blue-100">Cities Covered</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">4.8</div>
              <div className="text-blue-100 flex items-center justify-center">
                <FaStar className="mr-1" />
                Average Rating
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="container mx-auto px-3 sm:px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-gray-800">
            Ready to Start Your Journey?
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8">
            Join thousands of riders and passengers who are already saving money and making friends.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            {!isAuthenticated ? (
              <>
                <Link to="/login" className="btn-secondary text-base sm:text-lg px-6 sm:px-8 py-3">
                  Login
                </Link>
                <Link to="/register" className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3">
                  Sign Up Now
                </Link>
              </>
            ) : (
              <>
                <Link to="/register" className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3">
                  Offer a Ride
                </Link>
                <button 
                  onClick={() => {
                    document.querySelector('form')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-secondary text-base sm:text-lg px-6 sm:px-8 py-3"
                >
                  Browse Available Rides
                </button>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
